import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, Mail, Plus, Settings } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateWorkspace, useMyInvitations, useWorkspaces } from "@/hooks/use-workspace";
import { applyServerErrors } from "@/lib/core/form-errors";
import { useCurrentWorkspace } from "@/lib/workspace/workspace-context";
import { PlanBadge } from "@/components/upgrade/plan-badge";
import { useOwnPlan } from "@/hooks/use-plan";
import { requestUpgrade } from "@/lib/plans/upgrade";

/** "/invoices/abc" -> "/invoices": a record of the old workspace does not exist in the new one. */
const sectionOf = (pathname: string) => `/${pathname.split("/")[1] ?? ""}`;

/** Header menu: the current workspace, switching, creating one, settings and invitations. */
export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { workspaceId, switchWorkspace } = useCurrentWorkspace();
  const workspaces = useWorkspaces();
  const invitations = useMyInvitations();
  const [creating, setCreating] = useState(false);
  // New workspaces come with the user's own plan, not the one of the workspace they are in.
  const own = useOwnPlan();
  const current = workspaces.data?.find((workspace) => workspace.id === workspaceId);
  const pending = invitations.data?.length ?? 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="max-w-40 min-w-0 shrink gap-1.5 md:max-w-48"
            aria-label={t("workspace.switcher.label")}
            data-testid="workspace-switcher"
          >
            {current ? (
              <span className="truncate">{current.name}</span>
            ) : (
              <Skeleton className="h-4 w-24" data-testid="workspace-switcher-loading" />
            )}
            {pending > 0 && <span className="size-1.5 rounded-full bg-primary" aria-hidden />}
            <ChevronsUpDown className="opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>{t("workspace.switcher.heading")}</DropdownMenuLabel>
          {workspaces.data?.map((workspace) => (
            <DropdownMenuItem
              key={workspace.id}
              onSelect={() => {
                if (workspace.id === workspaceId) return;
                switchWorkspace(workspace.id);
                navigate(sectionOf(pathname) === "/settings" ? pathname : sectionOf(pathname));
              }}
              data-testid="workspace-option"
            >
              <span className="flex-1 truncate">{workspace.name}</span>
              {workspace.id === workspaceId && <Check />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() =>
              own.limits.workspaces ? setCreating(true) : requestUpgrade("workspaces")
            }
            data-testid="new-workspace"
          >
            <Plus /> <span className="flex-1">{t("workspace.switcher.new")}</span>
            {!own.isLoading && !own.limits.workspaces && <PlanBadge plan="pro" />}
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/workspace">
              <Settings /> {t("workspace.switcher.settings")}
            </Link>
          </DropdownMenuItem>
          {pending > 0 && (
            <DropdownMenuItem asChild data-testid="invitations-link">
              <Link to="/invitations">
                <Mail /> {t("workspace.switcher.invitations", { count: pending })}
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <NewWorkspaceDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => navigate("/dashboard")}
      />
    </>
  );
}

function NewWorkspaceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateWorkspace();
  const schema = z.object({ name: z.string().trim().min(1, t("workspace.nameRequired")).max(200) });
  type Values = z.infer<typeof schema>;
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: "" } });

  const submit = async (values: Values) => {
    try {
      await create.mutateAsync(values.name);
      toast.success(t("workspace.switcher.created"));
      form.reset();
      onOpenChange(false);
      onCreated();
    } catch (error) {
      const message = applyServerErrors(error, (field, err) => form.setError(field as "name", err));
      if (message) form.setError("name", { message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("workspace.switcher.new")}</DialogTitle>
          <DialogDescription>{t("workspace.switcher.newHint")}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(submit)}
            className="space-y-4"
            data-testid="new-workspace-form"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("workspace.name")}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={create.isPending}>
                {t("workspace.switcher.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
